// src/components/onboarding/SignaturePad.tsx
//
// Lightweight canvas-based signature pad (no external dependency).
// Supports mouse + touch. Exposes onChange(dataUrl|null) where dataUrl is a
// PNG data URL, or null when the pad is empty/cleared.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';

interface SignaturePadProps {
    onChange: (dataUrl: string | null) => void;
    height?: number;
}

const SignaturePad: React.FC<SignaturePadProps> = ({ onChange, height = 200 }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const hasDrawnRef = useRef(false);
    const [empty, setEmpty] = useState(true);

    // Configure canvas resolution to match its rendered size (crisp on retina).
    const setupCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#1a1a1a';
    }, []);

    useEffect(() => {
        setupCanvas();
        window.addEventListener('resize', setupCanvas);
        return () => window.removeEventListener('resize', setupCanvas);
    }, [setupCanvas]);

    const pointFromEvent = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        if ('touches' in e) {
            const t = e.touches[0] || e.changedTouches[0];
            return { x: t.clientX - rect.left, y: t.clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        drawingRef.current = true;
        const { x, y } = pointFromEvent(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const move = (e: React.MouseEvent | React.TouchEvent) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        const { x, y } = pointFromEvent(e);
        ctx.lineTo(x, y);
        ctx.stroke();
        if (!hasDrawnRef.current) {
            hasDrawnRef.current = true;
            setEmpty(false);
        }
    };

    const end = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        const canvas = canvasRef.current;
        if (canvas && hasDrawnRef.current) {
            onChange(canvas.toDataURL('image/png'));
        }
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        hasDrawnRef.current = false;
        setEmpty(true);
        onChange(null);
    };

    return (
        <div>
            <div className="relative rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 overflow-hidden">
                <canvas
                    ref={canvasRef}
                    style={{ width: '100%', height, touchAction: 'none' }}
                    className="block cursor-crosshair"
                    onMouseDown={start}
                    onMouseMove={move}
                    onMouseUp={end}
                    onMouseLeave={end}
                    onTouchStart={start}
                    onTouchMove={move}
                    onTouchEnd={end}
                />
                {empty && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <span className="text-gray-300 text-sm font-medium select-none">Sign here</span>
                    </div>
                )}
            </div>
            <div className="mt-2 flex justify-end">
                <button
                    type="button"
                    onClick={clear}
                    disabled={empty}
                    className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-brand disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <Eraser className="w-3.5 h-3.5" /> Clear
                </button>
            </div>
        </div>
    );
};

export default SignaturePad;
